import { useEffect, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    Typography,
} from "@mui/material";
import { MailOutline, PhonelinkLock } from "@mui/icons-material";
import OtpInput from "react-otp-input";
import { toast } from "react-toastify";

import {
    reenviarCodigoDosFactor,
    verificarDosFactor,
} from "@/shared/actions/auth/dos-factor.actions";
import { useAuthStore } from "@/store/auth/auth.store";
import { useDosFactorLogin } from "@/store/auth/dos-factor-login.store";
import { useMfaStatusStore } from "@/store/auth/mfa-status.store";
import { useUserModulesStore } from "@/store/auth/user-modules.store";
import { useUserPermissionsStore } from "@/store/auth/user-permissions.store";
import { useUiStore } from "@/store/ui/ui.store";

export default function DosFactorLoginDialog() {
    const {
        isOpen,
        challengeToken,
        metodo,
        emailMascarado,
        emailEnvioOk,
        emailEnvioError,
        setEmailEnvioOk,
        loginData,
        close,
    } = useDosFactorLogin();

    const setAuth = useAuthStore(s => s.setAuth);
    const setPermissions = useUserPermissionsStore(s => s.setPermissions);
    const setModulos = useUserModulesStore(s => s.setModules);
    const setIsGlobalLoading = useUiStore(s => s.setIsGlobalLoading);

    const [codigo, setCodigo] = useState("");
    const [usaBackup, setUsaBackup] = useState(false);
    const [verificando, setVerificando] = useState(false);
    const [reenviando, setReenviando] = useState(false);
    const [mostrarEmail, setMostrarEmail] = useState<string | null>(emailMascarado);

    // reset state al abrir
    useEffect(() => {
        if (isOpen) {
            setCodigo("");
            setUsaBackup(false);
            setMostrarEmail(emailMascarado);
        }
    }, [isOpen, emailMascarado]);

    if (!isOpen || !challengeToken || !metodo) return null;

    const onVerificar = async () => {
        const codeTrim = codigo.trim();
        if (usaBackup) {
            // Backup code: aceptar cualquier formato con 10 alfanumericos
            // (ej: "ABCDE-12345", "ABCDE12345", "abcde 12345").
            const soloAlfa = codeTrim.replace(/[^A-Za-z0-9]/g, "");
            if (soloAlfa.length !== 10) {
                toast.error("El código de respaldo debe tener 10 caracteres (formato XXXXX-XXXXX).");
                return;
            }
        } else if (codeTrim.length !== 6) {
            toast.error("Ingresa los 6 dígitos del código");
            return;
        }
        setVerificando(true);
        setIsGlobalLoading(true);
        try {
            const res: any = await verificarDosFactor({
                challenge_token: challengeToken,
                codigo: codeTrim,
                usa_backup: usaBackup,
                // Si el login original venía con forzar:true (reemplazo de
                // sesión activa), propagarlo para que finalizar_login borre
                // el token previo y emita uno nuevo. Sin esto, el usuario
                // entraría reutilizando el token de la sesión vieja.
                forzar: Boolean((loginData as any)?.forzar),
            });
            const respData = res?.data;
            if (!respData?.token) {
                toast.error(res?.message || "No se pudo completar el inicio de sesión");
                return;
            }
            setAuth(respData.token, {
                ...respData.profile,
                email: respData?.email,
                username: respData?.username,
                user_id: respData?.id,
            });
            setPermissions(respData.permisos);
            setModulos(respData.modulos);
            useMfaStatusStore.getState().setStatus(
                Boolean(respData?.mfa_setup_pendiente),
                respData?.mfa_grace_until ?? null,
            );
            toast("Inicio de sesión exitoso", { type: "success" });
            close();
        } catch (err: any) {
            // Mensajes posibles del backend (api/vistas/usuario/dos_factor_view.py):
            //   - "Código inválido."
            //   - "Sesión de verificación expirada. Inicia sesión nuevamente."
            //   - "Demasiados intentos. Vuelve a iniciar sesión."
            const status = err?.response?.status;
            const apiMsg = err?.response?.data?.message;
            let mensaje = apiMsg || err?.message;
            if (!mensaje) {
                mensaje = usaBackup
                    ? "Código de respaldo inválido. Revisa el formato XXXXX-XXXXX e intenta de nuevo."
                    : "Código incorrecto. Verifica los 6 dígitos e intenta de nuevo.";
            }
            if (status === 429) {
                mensaje = "Demasiados intentos. Vuelve a iniciar sesión.";
            }
            toast.error(mensaje);
            setCodigo("");
        } finally {
            setVerificando(false);
            setIsGlobalLoading(false);
        }
    };

    const onReenviar = async () => {
        if (!challengeToken) return;
        setReenviando(true);
        try {
            const res = await reenviarCodigoDosFactor(challengeToken);
            setMostrarEmail(res?.email_mascarado ?? mostrarEmail);
            // Si antes había fallado el envío, ahora se considera OK.
            setEmailEnvioOk(true, null);
            toast.success("Código reenviado a tu correo");
        } catch (err: any) {
            const apiMsg = err?.response?.data?.message;
            setEmailEnvioOk(false, apiMsg || "Error reenviando código");
            toast.error(
                apiMsg ||
                "No se pudo reenviar el código. Revisa tu conexión o contacta soporte.",
            );
        } finally {
            setReenviando(false);
        }
    };

    const titulo = usaBackup
        ? "Código de respaldo"
        : metodo === "app"
            ? "Verificación con App"
            : "Verificación por Email";

    return (
        <Dialog open={isOpen} disableEscapeKeyDown maxWidth="xs" fullWidth onClose={() => { /* bloqueado */ }}>
            <DialogTitle>
                <Stack direction="row" alignItems="center" spacing={1}>
                    {metodo === "app" ? <PhonelinkLock color="primary" /> : <MailOutline color="primary" />}
                    <Typography variant="h6">{titulo}</Typography>
                </Stack>
            </DialogTitle>
            <DialogContent dividers>
                <Stack spacing={2}>
                    {!usaBackup && metodo === "app" && (
                        <Typography variant="body2" color="text.secondary">
                            Ingresa el código de 6 dígitos que muestra tu app autenticadora
                            (Google / Microsoft / Duo).
                        </Typography>
                    )}
                    {!usaBackup && metodo === "email" && (
                        <Typography variant="body2" color="text.secondary">
                            Enviamos un código de 6 dígitos a {mostrarEmail ? <strong>{mostrarEmail}</strong> : "tu correo"}.
                            Ingrésalo a continuación.
                        </Typography>
                    )}
                    {!usaBackup && metodo === "email" && emailEnvioOk === false && (
                        <Alert severity="error" variant="filled">
                            <strong>No pudimos enviar el código a tu correo.</strong><br />
                            {emailEnvioError || "Revisa tu conexión o usa un código de respaldo."}
                            <br />
                            Intenta nuevamente con <em>Reenviar código</em> o utiliza un código de respaldo.
                        </Alert>
                    )}
                    {usaBackup && (
                        <Alert severity="info" variant="outlined">
                            Ingresa uno de los códigos de respaldo (formato XXXXX-XXXXX).
                            Cada código sirve una sola vez.
                        </Alert>
                    )}

                    <Box display="flex" justifyContent="center" py={1}>
                        {usaBackup ? (
                            <input
                                value={codigo}
                                onChange={(e) => setCodigo(e.target.value.toUpperCase())}
                                placeholder="XXXXX-XXXXX"
                                autoFocus
                                spellCheck={false}
                                autoCapitalize="characters"
                                style={{
                                    textAlign: "center",
                                    fontFamily: "monospace",
                                    fontSize: 20,
                                    letterSpacing: 2,
                                    padding: "12px 16px",
                                    borderRadius: 6,
                                    border: "1px solid #ccc",
                                    width: "100%",
                                    maxWidth: 260,
                                    outline: "none",
                                }}
                            />
                        ) : (
                            <OtpInput
                                value={codigo}
                                onChange={setCodigo}
                                numInputs={6}
                                shouldAutoFocus
                                renderInput={(props) => (
                                    <input
                                        {...props}
                                        style={{
                                            width: 40,
                                            height: 48,
                                            margin: "0 4px",
                                            fontSize: 20,
                                            textAlign: "center",
                                            borderRadius: 6,
                                            border: "1px solid #ccc",
                                            outline: "none",
                                        }}
                                        inputMode="numeric"
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter" && codigo.length === 6) {
                                                onVerificar();
                                            }
                                            props.onKeyDown?.(e);
                                        }}
                                    />
                                )}
                            />
                        )}
                    </Box>

                    <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1} flexWrap="wrap" rowGap={1}>
                        {metodo === "email" && !usaBackup ? (
                            <Button
                                size="small"
                                variant="text"
                                onClick={onReenviar}
                                disabled={reenviando}
                            >
                                {reenviando ? "Reenviando..." : "Reenviar código"}
                            </Button>
                        ) : <span />}
                        <Button
                            size="small"
                            variant="text"
                            onClick={() => {
                                setUsaBackup((v) => !v);
                                setCodigo("");
                            }}
                        >
                            {usaBackup ? "← Volver" : "Usar código de respaldo"}
                        </Button>
                    </Stack>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={close} color="inherit">
                    Cancelar
                </Button>
                <Button
                    variant="contained"
                    onClick={onVerificar}
                    disabled={verificando}
                >
                    {verificando ? "Verificando..." : "Verificar"}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

